import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

// Material UI
import { styled, Toolbar, Box, Drawer, IconButton } from '@mui/material';
import { List, ListItem, ListItemIcon, ListItemText, Divider as _Divider } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import EventIcon from '@mui/icons-material/Event';
import SearchIcon from '@mui/icons-material/Search';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CableIcon from '@mui/icons-material/Cable';

// Context
import { useDrawer } from 'context/drawer';

/**
 * Desktop Only
 * See AdminTabs.js for the mobile version of this menu
 */

const DRAWER_WIDTH = 250;
const DRAWER_WIDTH_COLLAPSED = 64;

export default function AdminDrawer() {
    const history = useHistory();
    const { isCollapsed, toggleDrawer } = useDrawer();

    const dashboard = useRouteMatch({ path: '/admin/', exact: true });
    const events = useRouteMatch({ path: '/admin/events' });
    const ops = useRouteMatch({ path: '/admin/operations' });
    const orders = useRouteMatch({ path: '/admin/orders' });
    const errorLog = useRouteMatch({ path: '/admin/error_log' });
    const hosts = useRouteMatch({ path: '/admin/hosts' });
    const users = useRouteMatch({ path: '/admin/users' });
    const connect = useRouteMatch({ path: '/admin/connect' });
    const notifications = useRouteMatch({ path: '/admin/notifications' });
    const reports = useRouteMatch({ path: '/admin/reports' });
    const calculator = useRouteMatch({ path: '/admin/calculator' });
    const mcp = useRouteMatch({ path: '/admin/mcp' });

    const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

    return (
        <Box displayPrint="none">
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexGrow: 0,
                    transition: (theme) =>
                        theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: isCollapsed
                                ? theme.transitions.duration.leavingScreen
                                : theme.transitions.duration.enteringScreen,
                        }),
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        transition: (theme) =>
                            theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: isCollapsed
                                    ? theme.transitions.duration.leavingScreen
                                    : theme.transitions.duration.enteringScreen,
                            }),
                        overflowX: 'hidden',
                    },
                }}
            >
                <Toolbar />

                <Box
                    sx={{
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        backgroundColor: (theme) =>
                            process.env.REACT_APP_ENV === 'development'
                                ? theme.palette.grey[600]
                                : theme.palette.background.dark,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <List sx={{ flexGrow: 1 }}>
                        <MenuItem
                            title="Dashboard"
                            icon={<DashboardIcon />}
                            active={Boolean(dashboard)}
                            disabled={Boolean(dashboard?.isExact)}
                            onClick={() => history.push('/admin/')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Ops"
                            icon={<EventIcon />}
                            active={Boolean(ops)}
                            disabled={Boolean(ops?.isExact)}
                            onClick={() => history.push('/admin/operations')}
                            isCollapsed={isCollapsed}
                        />

                        <Divider />

                        <MenuItem
                            title="Hosts"
                            icon={<BusinessIcon />}
                            active={Boolean(hosts)}
                            disabled={Boolean(hosts?.isExact)}
                            onClick={() => history.push('/admin/hosts')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Events"
                            icon={<EventIcon />}
                            active={Boolean(events)}
                            disabled={Boolean(events?.isExact)}
                            onClick={() => history.push('/admin/events')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Users"
                            icon={<PersonIcon />}
                            active={Boolean(users)}
                            disabled={Boolean(users?.isExact)}
                            onClick={() => history.push('/admin/users')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Connect"
                            icon={<MonetizationOnIcon />}
                            active={Boolean(connect)}
                            disabled={Boolean(connect?.isExact)}
                            onClick={() => history.push('/admin/connect')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Notifications"
                            icon={<MailOutlineIcon />}
                            active={Boolean(notifications)}
                            disabled={Boolean(notifications?.isExact)}
                            onClick={() => history.push('/admin/notifications')}
                            isCollapsed={isCollapsed}
                        />

                        <Divider />

                        <MenuItem
                            title="Order Lookup"
                            icon={<SearchIcon />}
                            active={Boolean(orders)}
                            disabled={Boolean(orders?.isExact)}
                            onClick={() => history.push('/admin/orders')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Error Log"
                            icon={<ErrorOutlineOutlinedIcon />}
                            active={Boolean(errorLog)}
                            disabled={Boolean(errorLog?.isExact)}
                            onClick={() => history.push('/admin/error_log')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Reports"
                            icon={<GetAppIcon />}
                            active={Boolean(reports)}
                            disabled={Boolean(reports?.isExact)}
                            onClick={() => history.push('/admin/reports')}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Calculator"
                            icon={<LocalAtmIcon />}
                            active={Boolean(calculator)}
                            disabled={Boolean(calculator?.isExact)}
                            onClick={() => history.push('/admin/calculator')}
                            isCollapsed={isCollapsed}
                        />
                        <MenuItem
                            title="AI connections"
                            icon={<CableIcon />}
                            active={Boolean(mcp)}
                            disabled={Boolean(mcp?.isExact)}
                            onClick={() => history.push('/admin/mcp')}
                            isCollapsed={isCollapsed}
                        />
                    </List>

                    <Box
                        sx={{
                            borderTop: 1,
                            borderColor: (theme) => theme.palette.grey[700],
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1,
                        }}
                    >
                        <IconButton
                            onClick={toggleDrawer}
                            sx={{
                                color: (theme) => theme.palette.common.white,
                            }}
                        >
                            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}

/**
 * Menu Item Props:
 *  active: The menu item is highlighted
 *  disabled: The menu item can't be clicked on
 *  isCollapsed: Whether the drawer is in collapsed/mini mode
 */

function MenuItem(props) {
    const { active, disabled, onClick, icon, title, isCollapsed } = props;

    const IconComponent = active ? ListItemIconActive : ListItemIconInactive;
    const TextComponent = active ? ListItemTextActive : ListItemTextInactive;

    return (
        <ListItem
            onClick={disabled ? undefined : onClick}
            sx={{
                paddingLeft: isCollapsed ? 2.5 : 4,
                paddingRight: isCollapsed ? 2.5 : 4,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[700],
                },
            }}
        >
            <IconComponent sx={{ minWidth: isCollapsed ? 'auto' : 56 }}>{icon}</IconComponent>
            <TextComponent
                primary={title}
                sx={{
                    opacity: isCollapsed ? 0 : 1,
                    whiteSpace: 'nowrap',
                    transition: (theme) =>
                        theme.transitions.create('opacity', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.shorter,
                        }),
                }}
            />
        </ListItem>
    );
}

const ListItemIconActive = styled(ListItemIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const ListItemTextActive = styled(ListItemText)(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const ListItemIconInactive = styled(ListItemIcon)(({ theme }) => ({
    color: theme.palette.common.white,
}));

const ListItemTextInactive = styled(ListItemText)(({ theme }) => ({
    color: theme.palette.common.white,
}));

const Divider = styled(_Divider)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
}));
